 "use client";

import { AuthenticatedUser, Office } from '@gettactic/api';
import { TacticApiClient } from '@gettactic/api';
import { getDeviceTZ } from '@gettactic/helpers/src/intl/getDeviceTZ';
import Cookies from 'js-cookie';
import React, { useEffect, useReducer } from 'react';

type Action =
  | { type: 'updateCurrentOffice'; payload: string }
  | { type: 'updateDeviceTz'; payload: string }
  | { type: 'updateAuthUser'; payload: AuthenticatedUser };

type Dispatch = (action: Action) => void;

type State = {
  authenticatedUser: AuthenticatedUser;
  deviceTz: string;
  currentOffice: Office;
};

export interface AppAuthenticatedContextProps {
  userContext: State;
  setUserContext: Dispatch;
}

export const AppAuthenticatedContext = React.createContext<
  AppAuthenticatedContextProps | undefined
>(undefined);

function userContextReducer(state: State, action: Action): State {
  switch (action.type) {
    case 'updateAuthUser': {
      const newUser = action.payload;
      const newOffices = newUser?.offices?.offices ?? [];
      // If the current office no longer exists in the new user's offices
      // (e.g. after an org switch), reset to the new user's default office.
      const currentStillValid = newOffices.some(
        (o) => o.id === state.currentOffice?.id
      );
      const nextOffice = currentStillValid
        ? state.currentOffice
        : newOffices.find((o) => o.id === newUser?.offices?.officeId) ??
          newUser?.offices?.office ??
          newOffices[0] ??
          state.currentOffice;
      return {
        ...state,
        authenticatedUser: newUser,
        currentOffice: nextOffice
      };
    }
    case 'updateCurrentOffice': {
      const officeMatch = (
        state.authenticatedUser?.offices?.offices || []
      ).find((x) => x.id === action.payload);
      if (!officeMatch) {
        // Invalid office ID (e.g. from URL param or stale cookie) — keep current state
        return state;
      }
      Cookies.set('__selected__office', action.payload);
      return {
        ...state,
        currentOffice: officeMatch
      };
    }
    case 'updateDeviceTz': {
      return {
        ...state,
        deviceTz: action.payload
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action}`);
    }
  }
}

export interface AppAuthenticatedProviderProps extends React.PropsWithChildren {
  authenticatedUser: AuthenticatedUser;
  api: TacticApiClient;
  cookieOffice?: string;
}

export const AppAuthenticatedProvider: React.FC<
  AppAuthenticatedProviderProps
> = ({ authenticatedUser, children, api, cookieOffice: forceOffice }) => {
  // TODO: Might need to revisit this. Not sure if this will cause too many unnecessary rerenders
  useEffect(() => {
    action({ type: 'updateAuthUser', payload: authenticatedUser });
  }, [authenticatedUser]);

  // Once we know the authenticated user, need to update the api-client
  // since it now will need the organization slug to append the organization header
  if (authenticatedUser) {
    api.updateUser(authenticatedUser);
  }

  const cookieOffice = forceOffice || Cookies.get('__selected__office');
  const selectedCookieOffice = authenticatedUser?.offices?.offices.find(
    (id) => id.id === cookieOffice
  );

  // Initialize officeId with the user's defaultOfficeId. If no default set, use the first in the list.
  // If no default & no offices in the list, this user should be in the setup wizard or in a visitor-only page
  const defaultOrFirstOffice =
    selectedCookieOffice ||
    (authenticatedUser?.offices?.officeId
      ? authenticatedUser?.offices?.offices?.find(
          (x) => x.id === authenticatedUser?.offices?.officeId
        )
      : authenticatedUser?.offices
        ? authenticatedUser?.offices.office // User Default
        : authenticatedUser?.offices?.offices[0]); // First office in the list

  if (!defaultOrFirstOffice) {
    throw new Error('No office found');
  }

  const [state, action] = useReducer(userContextReducer, {
    authenticatedUser: authenticatedUser,
    deviceTz:
      getDeviceTZ() ??
      defaultOrFirstOffice.time_zone ??
      authenticatedUser?.user?.time_zone ??
      'UTC',
    currentOffice: defaultOrFirstOffice
  });

  const value = {
    userContext: state,
    setUserContext: action
  };

  return (
    <AppAuthenticatedContext.Provider value={value}>
      {children}
    </AppAuthenticatedContext.Provider>
  );
};
